<script setup lang="ts">
/** 📖 v7.04 (per Javi): the guide a tester "couldn't find" - a real door
 * on the title and in the pause menu, not a collapsed fold. The three
 * beats of the loop first, then the five lines the fold used to hold.
 * 🖼️ v7.09 (Javi: "the text lands outside the modal"): the card keeps the
 * ornate frame's own cap and foot padding (a flat 22px put the title in
 * the crest and the button on the foot), and the lists scroll in an inner
 * pane so nothing ever rides under the painted crest (the v5.58 rule). */
import { RUN_STORAGE_KEY } from '~/composables/useRun'
import { tutorialAlreadyDone } from '~/composables/useTutorial'

const emit = defineEmits<{ close: []; practice: [] }>()
const { t } = useLocale()
const sfx = useSfx()
const dismiss = useBackdropDismiss(() => emit('close'))

/** 🎓 v7.40 (per Javi, from the parked onboarding WIP): the practice again,
 * from here. Only with no campaign run in progress - the practice lives in
 * the campaign's save slot - so a player mid-run is told to finish or
 * abandon it first; inside the practice (or a Puzzle Game) it is not offered
 * at all. A live weekly is stashed and comes back when the practice ends. A
 * replay never pays the Stardust twice, and a player who finished it before
 * counts nothing in the funnel ('guide:replay', useRun.startFirstDispatch).
 * The big blue bar is for new players; one who finished it gets a small link
 * (the proposal). "Got it" stays the main button. */
const { run, startFirstDispatch } = useRun()
const practiceOffer = computed<'offer' | 'busy' | 'none'>(() => {
  const live = run.value
  if (live?.mode === 'tutorial' || typeof live?.puzzleLevel === 'number') return 'none'
  if (live && live.mode !== 'daily') return 'busy'
  try {
    const saved = JSON.parse(localStorage.getItem(RUN_STORAGE_KEY) ?? 'null') as { mode?: string } | null
    if (saved && saved.mode !== 'tutorial') return 'busy'
  } catch { /* an unreadable slot holds no run to protect */ }
  return 'offer'
})
const practiceDone = tutorialAlreadyDone()
const practiceError = ref('')
async function playPractice() {
  sfx.confirm()
  try {
    startFirstDispatch({ replay: true, preserveRun: run.value?.mode === 'daily' })
  } catch {
    practiceError.value = t('menu.startFailed')
    return
  }
  emit('practice')
  emit('close')
  // On /encounter this is a no-op: the page sets the swapped-in practice up itself.
  await navigateTo('/encounter')
}
</script>

<template>
  <Teleport to="body">
    <div class="guide-backdrop" data-testid="how-to-play" @pointerdown="dismiss.down" @pointerup="dismiss.up" @pointercancel="dismiss.cancel">
      <div class="card card-ornate guide-card" role="dialog" aria-modal="true" aria-labelledby="guide-title" data-interactive>
        <ModalClose @close="sfx.tap(); emit('close')"/>
        <h2 id="guide-title" class="guide-title"><img src="/ui/icon-scouting.webp" alt="" class="guide-title-icon" draggable="false"> {{ t('guide.title') }}</h2>
        <div class="guide-scroll" data-testid="how-to-play-scroll">
          <ol class="guide-loop">
            <li><IconText :text="t('guide.loop1')" /></li>
            <li><IconText :text="t('guide.loop2')" /></li>
            <li><IconText :text="t('guide.loop3')" /></li>
          </ol>
          <ul class="guide-lines">
            <!-- v7.10 wording pass: how2/how3 restated the loop above and are not shown. -->
            <li><IconText :text="t('title.how1')" /></li>
            <li><IconText :text="t('title.how4')" /></li>
            <li><IconText :text="t('title.how5')" /></li>
            <!-- 🎓 v7.20 (tutorial review): the odds rule and the field controls, retrievable here. -->
            <li><IconText :text="t('guide.odds')" /></li>
            <!-- 🎓 v8.02: Surge, the on-use skills and the glass, as the practice teaches them. -->
            <li><IconText :text="t('guide.surge')" /></li>
            <li><IconText :text="t('guide.controls')" /></li>
          </ul>
        </div>
        <button class="btn guide-close" data-testid="how-to-play-close" @click="sfx.tap(); emit('close')">{{ t('common.gotIt') }}</button>
        <button v-if="practiceOffer === 'offer' && !practiceDone" class="btn btn-secondary btn-bar guide-practice" data-testid="how-to-play-practice" @click="playPractice">{{ t('guide.playPractice') }}</button>
        <button v-else-if="practiceOffer === 'offer'" class="guide-practice-link" data-testid="how-to-play-practice" @click="playPractice">{{ t('guide.playPractice') }}</button>
        <p v-else-if="practiceOffer === 'busy'" class="guide-practice-note" data-testid="how-to-play-practice-busy">{{ t('guide.practiceBusy') }}</p>
        <p v-if="practiceError" class="guide-practice-note" role="alert">{{ practiceError }}</p>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.guide-backdrop { position: fixed; inset: 0; z-index: 95; display: flex; align-items: center; justify-content: center; padding: 24px 16px; background: rgba(21, 41, 66, 0.6); }
/* The frame's cap/foot padding comes from .card-ornate; only the sides are ours.
 * max-height in svh, never vh alone (v6.26 lint): the veil is the short viewport. */
.guide-card {
  width: min(520px, 100%);
  max-height: calc(90vh - var(--safe-top) - var(--safe-bottom));
  max-height: calc(90svh - var(--safe-top) - var(--safe-bottom));
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-inline: 26px;
  overflow: hidden;
  text-align: left;
}
.guide-title { display: flex; align-items: center; justify-content: center; gap: 8px; margin: 0; font-size: 1.3rem; flex-shrink: 0; }
.guide-title-icon { width: 28px; height: 28px; object-fit: contain; }
.guide-scroll { min-height: 0; overflow-y: auto; padding: 2px 6px 4px 2px; scrollbar-width: thin; }
.guide-loop { margin: 0 0 12px; padding-left: 1.3em; font-weight: 700; line-height: 1.45; }
.guide-loop li { margin-bottom: 6px; }
.guide-lines { margin: 0; padding-left: 1.2em; font-size: 0.88rem; line-height: 1.45; color: var(--color-ink-soft); font-weight: 600; }
.guide-lines li { margin-bottom: 4px; }
.guide-close { flex-shrink: 0; width: min(280px, 100%); margin: 4px auto 0; }
/* 🎓 v7.40: the practice door - the standalone blue bar, under "Got it". */
/* (btn-bar's own 18/26/6 seat: the blue bar's face sits under its top gem.) */
.guide-practice { margin: 0 auto; }
/* A player who finished the practice: a small link, still a full 44px to tap. */
.guide-practice-link {
  flex-shrink: 0;
  align-self: center;
  min-height: 44px;
  padding: 0 10px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-ink-soft);
  text-decoration: underline;
  cursor: pointer;
}
.guide-practice-note { flex-shrink: 0; margin: 0; text-align: center; font-size: 0.82rem; font-weight: 600; color: var(--color-ink-soft); }
</style>
